import React from 'react';
import { Box, Typography, Divider } from '@mui/material';

interface BenchmarkTooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
    getMetricInfo: (metricKey: string) => { label: string; color: string; unit: string };
}

const BenchmarkTooltip: React.FC<BenchmarkTooltipProps> = ({ 
    active, 
    payload = [], 
    label, 
    getMetricInfo 
}) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    // Filter out any payload entries that are for "Alarms" or invalid entries
    const validPayload = payload.filter(item => {
        if (!item.dataKey) return false;
        const metricInfo = getMetricInfo(item.dataKey.replace('_benchmark', ''));
        return metricInfo && metricInfo.label !== 'Alarms';
    });

    if (validPayload.length === 0) {
        return null;
    }

    // Group payload by regular vs benchmark data
    const regularData = validPayload.filter(item => !item.dataKey?.includes('_benchmark'));
    const benchmarkData = validPayload.filter(item => item.dataKey?.includes('_benchmark'));

    const formatValue = (value: any, metricKey: string) => {
        const metricInfo = getMetricInfo(metricKey);
        
        if (typeof value === 'number') {
            if (metricInfo.unit === 'boolean' || metricKey.includes('output') || metricKey.includes('Available')) {
                return value === 1 ? 'ON' : 'OFF';
            }
            return value.toFixed(2);
        }
        return value;
    };

    const formatValueWithUnit = (value: any, metricKey: string) => {
        const formattedValue = formatValue(value, metricKey);
        const metricInfo = getMetricInfo(metricKey);
        
        if (formattedValue === 'ON' || formattedValue === 'OFF') {
            return formattedValue;
        }
        
        return metricInfo.unit && metricInfo.unit !== 'boolean' 
            ? `${formattedValue} ${metricInfo.unit}` 
            : formattedValue;
    };

    const formatTimestamp = (timestamp: string | number) => {
        const date = new Date(typeof timestamp === 'string' ? parseInt(timestamp) : timestamp);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
        });
    };

    return (
        <Box sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
            minWidth: 200,
            maxWidth: 400
        }}>
            {/* Timestamp */}
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                {formatTimestamp(label || '')}
            </Typography>

            {/* Regular data */}
            {regularData.length > 0 && (
                <Box sx={{ mb: benchmarkData.length > 0 ? 1 : 0 }}>
                    {/* Only show "Your Data:" label if there's also benchmark data */}
                    {benchmarkData.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            Your Data:
                        </Typography>
                    )}
                    {regularData.map((item, index) => {
                        const originalKey = item.dataKey;
                        const metricInfo = getMetricInfo(originalKey);
                        
                        return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 3,
                                        bgcolor: item.color,
                                        borderRadius: 1
                                    }}
                                />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {metricInfo.label}:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {formatValueWithUnit(item.value, originalKey)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Divider between regular and benchmark data */}
            {regularData.length > 0 && benchmarkData.length > 0 && (
                <Divider sx={{ my: 1 }} />
            )}

            {/* Benchmark data */}
            {benchmarkData.length > 0 && (
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Benchmark Average:
                    </Typography>
                    {benchmarkData.map((item, index) => {
                        const originalKey = item.dataKey.replace('_benchmark', '');
                        const metricInfo = getMetricInfo(originalKey);
                        
                        return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 2,
                                        backgroundImage: `repeating-linear-gradient(90deg, ${item.color} 0, ${item.color} 2px, transparent 2px, transparent 4px)`,
                                        borderRadius: 0
                                    }}
                                />
                                <Typography variant="body2" sx={{ flex: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                    {metricInfo.label}:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                    {formatValueWithUnit(item.value, originalKey)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Show comparison if both regular and benchmark data exist */}
            {regularData.length > 0 && benchmarkData.length > 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="primary.main" fontWeight="medium">
                        💡 Compare your values to the benchmark averages above
                    </Typography>
                </Box>
            )}

            {/* Show metric count for regular data only (when no benchmark data) */}
            {regularData.length > 1 && benchmarkData.length === 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                        {regularData.length} metrics at this time
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default BenchmarkTooltip;
